import { FC, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  Heading,
  Input,
  Text,
  Box,
  LoadableButton,
  Button,
  Flex,
  Alert,
  H2
} from '../../components';
import { useCurrentUser } from '../../lib/core/useCurrentUser';
import { useSystem } from '../../lib/core/useSystem';
import ErrorPage from '../ErrorPage';
import { useUpdateCurrentUserMutation } from '../../lib/core/mutations';
import { gql, useMutation } from '@apollo/client';
import {
  UserRfidAssignMethod,
  cancelUserRfidPairingMutation,
  cancelUserRfidPairingMutationVariables,
  requestUserRfidPairingByVehicleQrCodeMutation,
  requestUserRfidPairingByVehicleQrCodeMutationVariables
} from '../../core-types';

if (typeof window != 'undefined') {
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  var QrReader = require('modern-react-qr-reader');
}

const COUNTDOWN_SECONDS = 10;

enum RfidState {
  UNKNOWN,
  RFID_ASSIGNED,
  ENTER_RFID_OR_QR_CODE,
  SCAN_QR_CODE,
  SCAN_RFID,
  SCAN_RFID_TIMEOUT
}

export const REQUEST_RFID_PAIRING_MUTATION = gql`
  mutation requestUserRfidPairingByVehicleQrCodeMutation($qrCode: String!) {
    requestUserRfidPairingByVehicleQrCode(qrCode: $qrCode)
  }
`;

export const CANCEL_RFID_PAIRING_MUTATION = gql`
  mutation cancelUserRfidPairingMutation($qrCode: String!) {
    cancelUserRfidPairing(qrCode: $qrCode)
  }
`;

const AssignRfidCard: FC = () => {
  const intl = useIntl();

  const { rfidAssignMethod } = useSystem();
  const { currentUser, refetch, loading } = useCurrentUser();
  const [state, setState] = useState<RfidState>(RfidState.UNKNOWN);
  const [rfid, setRfid] = useState<string>('');
  const [qrCode, setQrCode] = useState<string>('');
  const [qrScanError, setQrScanError] = useState<boolean>(false);
  const [countdown, setCountdown] = useState<number>(COUNTDOWN_SECONDS);
  const [rfidOrQrError, setRfidOrQrError] = useState<string | undefined>();

  const [
    updateCurrentUser,
    { loading: updateRfidLoading }
  ] = useUpdateCurrentUserMutation({
    onCompleted: data => {
      setState(
        data?.updateCurrentUser?.rfid
          ? RfidState.RFID_ASSIGNED
          : RfidState.ENTER_RFID_OR_QR_CODE
      );
      setQrCode('');
      setRfid('');
    },
    onError: error => {
      setRfidOrQrError(error.message);
    }
  });

  const [
    requestRfidPairing,
    { loading: requestRfidPairingLoading }
  ] = useMutation<
    requestUserRfidPairingByVehicleQrCodeMutation,
    requestUserRfidPairingByVehicleQrCodeMutationVariables
  >(REQUEST_RFID_PAIRING_MUTATION, {
    onCompleted: data => {
      const timestamp = data.requestUserRfidPairingByVehicleQrCode;

      if (timestamp) {
        // Calculate number of seconds between now and the timestamp
        const seconds = Math.floor(
          (new Date(timestamp).getTime() - new Date().getTime()) / 1000
        );

        setCountdown(seconds);

        setState(RfidState.SCAN_RFID);
      } else
        setRfidOrQrError(
          intl.formatMessage({
            id: 'modules.Profile.AssignRfidCard.invalidQrCode',
            defaultMessage: 'Invalid QR code'
          })
        );
    },
    onError: error => {
      setRfidOrQrError(error.message);
    }
  });

  const [
    cancelRfidPairing,
    { loading: cancelRfidPairingLoading }
  ] = useMutation<
    cancelUserRfidPairingMutation,
    cancelUserRfidPairingMutationVariables
  >(CANCEL_RFID_PAIRING_MUTATION, {
    onCompleted: () => {
      setState(RfidState.UNKNOWN);
    }
  });

  useEffect(() => {
    if (currentUser) {
      switch (state) {
        case RfidState.UNKNOWN:
        case RfidState.SCAN_RFID:
        case RfidState.RFID_ASSIGNED:
          if (state === RfidState.UNKNOWN) {
            setRfid(currentUser.rfid || '');

            if ((currentUser.rfid || '') === '') {
              setQrCode('');
              setState(RfidState.ENTER_RFID_OR_QR_CODE);
            } else {
              setState(RfidState.RFID_ASSIGNED);
            }
          }
          break;

        default:
          break;
      }
    }
  }, [currentUser?.rfid, state]);

  useEffect(() => {
    const interval =
      state === RfidState.SCAN_RFID
        ? setInterval(() => {
            if (currentUser && currentUser.rfid !== null) {
              setState(RfidState.UNKNOWN);
            } else {
              refetch();

              setCountdown(value => value - 1);
            }
          }, 1000)
        : undefined;

    return () => clearInterval(interval);
  }, [state, currentUser?.rfid]);

  useEffect(() => {
    if (countdown <= 0) {
      setState(RfidState.SCAN_RFID_TIMEOUT);
    }
  }, [countdown]);

  const handleRemoveRfidCardClick = () => {
    updateCurrentUser({
      variables: {
        input: {
          rfid: null
        }
      }
    });
  };

  const handleQrCodeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const qrCode = event.target.value.toUpperCase();

    if (qrCode === '' || /^[0-9A-Z]+$/g.test(qrCode)) {
      setQrCode(qrCode);
    }
  };

  const handleRfidChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newRfid = event.target.value.toUpperCase();

    if (newRfid === '' || /^[0-9A-F]+$/g.test(newRfid)) {
      setRfid(newRfid);
    }
  };

  const handleCancelScanQrCodeClick = () => {
    setState(RfidState.UNKNOWN);
  };

  const handleCancelScanRfidClick = () => {
    cancelRfidPairing({
      variables: {
        qrCode
      }
    });

    setState(RfidState.UNKNOWN);
  };

  const startPairing = () => {
    requestRfidPairing({
      variables: {
        qrCode
      }
    });
  };

  const handleQrCodeNextButtonClick = () => {
    setRfidOrQrError(undefined);

    startPairing();
  };

  const handleSetRfidClick = () => {
    setRfidOrQrError(undefined);

    updateCurrentUser({
      variables: {
        input: {
          rfid
        }
      }
    });
  };

  const handleScanQrCodeClick = () => {
    setRfidOrQrError(undefined);
    setState(RfidState.SCAN_QR_CODE);
  };

  const handleEnterQrCodeClick = () => {
    setState(RfidState.ENTER_RFID_OR_QR_CODE);
  };

  const handleScan = (data: string) => {
    if (data) {
      setQrScanError(false);
      setQrCode(parseQrString(data));
      setState(RfidState.ENTER_RFID_OR_QR_CODE);
    }
  };

  const handleError = () => {
    setState(RfidState.ENTER_RFID_OR_QR_CODE);
    setQrScanError(true);
  };

  if (!currentUser) {
    return null;
  }

  const parseQrString = (str: string): string => {
    const equalsIndex = str.indexOf('=');

    if (equalsIndex === -1) {
      return str; // No '=' sign found, return the whole string
    } else {
      return str.substring(equalsIndex + 1); // Return everything after the '=' sign
    }
  };

  const getContent = () => {
    switch (state) {
      case RfidState.RFID_ASSIGNED:
        return (
          <>
            <Text as="p">
              {intl.formatMessage(
                {
                  id: 'modules.Profile.AssignRfidCard.rfidAssigned',
                  defaultMessage:
                    'You currently have RFID "{rfid}" assigned to your account.'
                },
                { rfid: currentUser.rfid }
              )}
            </Text>
            <LoadableButton
              responseTimeoutMs={0}
              responseState={'error'}
              loading={loading}
              onClick={handleRemoveRfidCardClick}
            >
              <FormattedMessage
                id="modules.Profile.AssignRfidCard.removeRfidCard"
                defaultMessage="Remove RFID card"
              />
            </LoadableButton>
          </>
        );

      case RfidState.ENTER_RFID_OR_QR_CODE:
        return (
          <>
            <Text as="p">
              <FormattedMessage
                id="modules.Profile.AssignRfidCard.intro1"
                defaultMessage="You can assign an RFID card to your account. Once assigned, you can use the RFID card to start a trip."
              />
            </Text>
            {rfidOrQrError && <Alert variant="error">{rfidOrQrError}</Alert>}
            {rfidAssignMethod?.some(
              value => value === UserRfidAssignMethod.set_rfid
            ) && (
              <>
                <H2>
                  <FormattedMessage
                    id="modules.Profile.AssignRfidCard.enterRfid"
                    defaultMessage="Enter RFID"
                  />
                </H2>
                <Text as="p">
                  <FormattedMessage
                    id="modules.Profile.AssignRfidCard.enterRfidSerial"
                    defaultMessage="Enter the RFID card serial number as it is displayed on the card"
                  />
                </Text>
                <Flex>
                  <Input
                    value={rfid}
                    disabled={updateRfidLoading}
                    style={{ minWidth: '120px' }}
                    maxLength={15}
                    onChange={handleRfidChange}
                    placeholder={intl.formatMessage({
                      id: 'modules.Profile.AssignRfidCard.rfid',
                      defaultMessage: 'RFID'
                    })}
                  />
                  <LoadableButton
                    responseTimeoutMs={0}
                    responseState={'error'}
                    style={{ whiteSpace: 'nowrap', marginLeft: '5px' }}
                    onClick={handleSetRfidClick}
                    loading={updateRfidLoading}
                    disabled={rfid === ''}
                  >
                    <FormattedMessage
                      id="modules.Profile.AssignRfidCard.setRfid"
                      defaultMessage="Set RFID"
                    />
                  </LoadableButton>
                </Flex>
              </>
            )}
            {rfidAssignMethod?.some(
              value => value === UserRfidAssignMethod.scan_qr
            ) && (
              <>
                <H2 style={{ marginTop: '20px' }}>
                  <FormattedMessage
                    id="modules.Profile.AssignRfidCard.useABikeToAssignRfid"
                    defaultMessage="Enter the RFID card serial number as it is displayed on the card"
                  />
                </H2>
                <Text as="p">
                  <FormattedMessage
                    id="modules.Profile.AssignRfidCard.enterQr"
                    defaultMessage="You need to be next to a bike and use the RFID reader to read your card. Enter the code shown next to the QR code on the bike. If you have a camera available on your device you can also scan the QR code."
                  />
                </Text>
                <Flex>
                  <Input
                    value={qrCode}
                    maxLength={15}
                    disabled={updateRfidLoading}
                    style={{ minWidth: '120px' }}
                    onChange={handleQrCodeChange}
                    placeholder={intl.formatMessage({
                      id: 'modules.Profile.AssignRfidCard.qrCode',
                      defaultMessage: 'QR code'
                    })}
                  />
                  <Button
                    shape="normal"
                    style={{ whiteSpace: 'nowrap', marginLeft: '5px' }}
                    disabled={updateRfidLoading}
                    onClick={handleScanQrCodeClick}
                  >
                    <FormattedMessage
                      id="modules.Profile.AssignRfidCard.scan"
                      defaultMessage="Scan"
                    />
                  </Button>
                  <LoadableButton
                    style={{ whiteSpace: 'nowrap', marginLeft: '5px' }}
                    loading={requestRfidPairingLoading}
                    onClick={handleQrCodeNextButtonClick}
                    disabled={qrCode === '' || updateRfidLoading}
                  >
                    <FormattedMessage
                      id="modules.Profile.AssignRfidCard.next"
                      defaultMessage="Next"
                    />
                  </LoadableButton>
                </Flex>
                {qrScanError && (
                  <Text as="p" color="red">
                    <FormattedMessage
                      id="modules.Profile.AssignRfidCard.qrScanError"
                      defaultMessage="Sorry, unable to activate the camera."
                    />
                  </Text>
                )}
              </>
            )}
          </>
        );

      case RfidState.SCAN_QR_CODE:
        return (
          <>
            <Text as="p">
              <FormattedMessage
                id="modules.Profile.AssignRfidCard.scanQr"
                defaultMessage="Scan the QR code on the bike."
              />
            </Text>
            <Flex flexDirection={'column'}>
              <Box style={{ maxWidth: '400px', width: '100%' }}>
                <Button
                  shape="normal"
                  style={{
                    whiteSpace: 'nowrap',
                    width: '100%',
                    marginBottom: '5px'
                  }}
                  onClick={handleEnterQrCodeClick}
                >
                  <FormattedMessage
                    id="WizardLayout.back"
                    defaultMessage="Back"
                  />
                </Button>
                <QrReader
                  delay={50}
                  facingMode={'environment'}
                  onError={handleError}
                  onScan={handleScan}
                  style={{ width: '100%' }}
                />
              </Box>
            </Flex>
          </>
        );

      case RfidState.SCAN_RFID:
        return (
          <>
            <Text as="p">
              <>
                <FormattedMessage
                  id="modules.Profile.AssignRfidCard.scanRfid"
                  defaultMessage="Please scan your RFID card on the bike."
                />
              </>
            </Text>
            <Flex alignItems={'center'} style={{ width: '100%' }}>
              <LoadableButton
                loading={cancelRfidPairingLoading}
                shape="normal"
                style={{ whiteSpace: 'nowrap', marginLeft: '5px' }}
                onClick={handleCancelScanRfidClick}
              >
                <FormattedMessage id="cancel" defaultMessage="Cancel" />
              </LoadableButton>
              <Text
                style={{
                  textAlign: 'center',
                  fontWeight: 'bold',
                  fontSize: '30px',
                  marginLeft: '10px'
                }}
              >
                {countdown}s
              </Text>
            </Flex>
          </>
        );

      case RfidState.SCAN_RFID_TIMEOUT:
        return (
          <>
            <Text as="p">
              <FormattedMessage
                id="modules.Profile.AssignRfidCard.scanRfidTimeout"
                defaultMessage="The RFID card was not scanned in time."
              />
            </Text>
            <Flex>
              <Button
                shape="normal"
                style={{ whiteSpace: 'nowrap', marginLeft: '5px' }}
                onClick={handleQrCodeNextButtonClick}
              >
                <FormattedMessage
                  id="modules.Profile.AssignRfidCard.tryAgain"
                  defaultMessage="Try again"
                />
              </Button>
              <Button
                shape="normal"
                style={{ whiteSpace: 'nowrap', marginLeft: '5px' }}
                onClick={handleCancelScanQrCodeClick}
              >
                <FormattedMessage id="cancel" defaultMessage="Cancel" />
              </Button>
            </Flex>
          </>
        );

      default:
        return <>Invalid page state</>;
    }
  };

  if (!rfidAssignMethod?.length) {
    return <ErrorPage statusCode={404} errorType="pageNotFound" />;
  }

  return (
    <div data-testid="Profile__AssignRfid">
      <Heading textAlign="center">
        <FormattedMessage
          id="Profile.sections.assignRfidCard"
          defaultMessage="Assign RFID card"
        />
      </Heading>
      <Box mb={6}>{getContent()}</Box>
    </div>
  );
};

export default AssignRfidCard;
